import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, TOKEN_KEY } from "./api";
import type { Tenant, User } from "./types";

type TenantLite = Pick<Tenant, "id" | "name" | "slug" | "branding" | "modules_enabled" | "is_active">;

interface AuthState {
  user: User | null;
  tenant: TenantLite | null;
  loading: boolean;
  login: (portal: "backoffice" | "client", email: string, password: string, rememberMe?: boolean) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasModule: (key: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<TenantLite | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(null);
      setTenant(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (portal: "backoffice" | "client", email: string, password: string, rememberMe = false) => {
    const { data } = await api.post("/auth/login", { email, password, portal, remember_me: rememberMe });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setTenant(data.tenant);
    return data.user as User;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setTenant(null);
  }, []);

  const hasModule = useCallback(
    (key: string) => (user?.user_type === "super_admin" ? true : !!tenant?.modules_enabled?.includes(key)),
    [user, tenant]
  );

  const value = useMemo(() => ({ user, tenant, loading, login, logout, refresh, hasModule }), [user, tenant, loading, login, logout, refresh, hasModule]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}
